import React, { useMemo, useRef } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Printer, FileText, TrendingUp, TrendingDown, Target, AlertTriangle } from "lucide-react";

const mesesLabel = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
const mesesKeys = ["valor_jan", "valor_fev", "valor_mar", "valor_abr", "valor_mai", "valor_jun",
                   "valor_jul", "valor_ago", "valor_set", "valor_out", "valor_nov", "valor_dez"];

export default function RelatorioOrcamentario({ orcamentos, lancamentos, custosAlocados, ano, propriedade }) {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  // Calcular realizado por categoria e mês
  const realizadoPorCategoria = useMemo(() => {
    const resultado = {};

    lancamentos.forEach(l => {
      const dataLanc = new Date(l.data);
      if (dataLanc.getFullYear() !== ano) return;
      
      const mes = dataLanc.getMonth();
      const cat = l.categoria || "Outros";
      const tipo = l.tipo;
      const key = `${tipo}_${cat}`;

      if (!resultado[key]) {
        resultado[key] = { tipo, categoria: cat, meses: Array(12).fill(0), total: 0 };
      }
      resultado[key].meses[mes] += l.valor || 0;
      resultado[key].total += l.valor || 0;
    });

    custosAlocados.forEach(c => {
      const dataC = new Date(c.data);
      if (dataC.getFullYear() !== ano) return;
      
      const mes = dataC.getMonth();
      const cat = c.categoria || "Outros";
      const key = `despesa_${cat}`;

      if (!resultado[key]) {
        resultado[key] = { tipo: "despesa", categoria: cat, meses: Array(12).fill(0), total: 0 };
      }
      resultado[key].meses[mes] += c.valor || 0;
      resultado[key].total += c.valor || 0;
    });

    return resultado;
  }, [lancamentos, custosAlocados, ano]);

  // Dados do relatório
  const dadosRelatorio = useMemo(() => {
    const orcamentosFiltrados = orcamentos.filter(o => o.ano === ano && o.ativo !== false);

    const linhas = orcamentosFiltrados.map(orc => {
      const keyRealizado = `${orc.tipo}_${orc.categoria}`;
      const realizado = realizadoPorCategoria[keyRealizado] || { meses: Array(12).fill(0), total: 0 };

      const orcadoMensal = mesesKeys.map(k => orc[k] || 0);
      const realizadoMensal = realizado.meses;

      const diferencaMensal = orcadoMensal.map((o, i) => realizadoMensal[i] - o);
      const totalOrcado = orc.valor_total || 0;
      const totalRealizado = realizado.total;
      const diferenca = totalRealizado - totalOrcado;
      const desvio = totalOrcado > 0 ? (diferenca / totalOrcado) * 100 : 0;

      return {
        ...orc,
        orcadoMensal,
        realizadoMensal,
        diferencaMensal,
        totalOrcado,
        totalRealizado,
        diferenca,
        desvio
      };
    });

    const receitas = linhas.filter(l => l.tipo === 'receita');
    const despesas = linhas.filter(l => l.tipo === 'despesa');

    const totalReceitasOrcadas = receitas.reduce((a, l) => a + l.totalOrcado, 0);
    const totalReceitasRealizadas = receitas.reduce((a, l) => a + l.totalRealizado, 0);
    const totalDespesasOrcadas = despesas.reduce((a, l) => a + l.totalOrcado, 0);
    const totalDespesasRealizadas = despesas.reduce((a, l) => a + l.totalRealizado, 0);

    return {
      receitas,
      despesas,
      totalReceitasOrcadas,
      totalReceitasRealizadas,
      totalDespesasOrcadas,
      totalDespesasRealizadas,
      resultadoOrcado: totalReceitasOrcadas - totalDespesasOrcadas,
      resultadoRealizado: totalReceitasRealizadas - totalDespesasRealizadas
    };
  }, [orcamentos, realizadoPorCategoria, ano]);

  const handlePrint = () => {
    window.print();
  };

  const renderTabela = (linhas, tipo) => {
    if (linhas.length === 0) return null;

    const totalOrcado = linhas.reduce((a, l) => a + l.totalOrcado, 0);
    const totalRealizado = linhas.reduce((a, l) => a + l.totalRealizado, 0);
    const totalDiferenca = totalRealizado - totalOrcado;

    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2 border-stone-200">
              <th className="text-left py-2 px-2 font-bold">Categoria</th>
              {mesesLabel.map(m => (
                <th key={m} className="text-right py-2 px-1 font-bold text-xs">{m}</th>
              ))}
              <th className="text-right py-2 px-2 font-bold">Total</th>
              <th className="text-right py-2 px-2 font-bold">Desvio</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map(linha => (
              <React.Fragment key={linha.id}>
                {/* Linha Orçado */}
                <tr className="border-b border-stone-100 bg-stone-50">
                  <td className="py-1 px-2 font-medium" rowSpan={2}>{linha.categoria}</td>
                  {linha.orcadoMensal.map((val, idx) => (
                    <td key={idx} className="py-1 px-1 text-right text-xs text-stone-600">
                      {formatCurrency(val)}
                    </td>
                  ))}
                  <td className="py-1 px-2 text-right font-medium">{formatCurrency(linha.totalOrcado)}</td>
                  <td className="py-1 px-2 text-right" rowSpan={2}>
                    <Badge className={`${
                      Math.abs(linha.desvio) <= (linha.limite_alerta || 10) 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {linha.desvio >= 0 ? '+' : ''}{linha.desvio.toFixed(1)}%
                    </Badge>
                  </td>
                </tr>
                {/* Linha Realizado */}
                <tr className="border-b border-stone-200">
                  {linha.realizadoMensal.map((val, idx) => (
                    <td key={idx} className={`py-1 px-1 text-right text-xs font-medium ${tipo === 'receita' ? 'text-green-700' : 'text-red-700'}`}>
                      {formatCurrency(val)}
                    </td>
                  ))}
                  <td className={`py-1 px-2 text-right font-bold ${tipo === 'receita' ? 'text-green-700' : 'text-red-700'}`}>
                    {formatCurrency(linha.totalRealizado)}
                  </td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
          <tfoot>
            <tr className={`font-bold ${tipo === 'receita' ? 'bg-green-100' : 'bg-red-100'}`}>
              <td className="py-2 px-2">TOTAL {tipo === 'receita' ? 'RECEITAS' : 'DESPESAS'}</td>
              <td colSpan={12} className="py-2 px-2 text-right">
                Orçado: {formatCurrency(totalOrcado)} | Realizado: {formatCurrency(totalRealizado)}
              </td>
              <td className="py-2 px-2 text-right">{formatCurrency(totalDiferenca)}</td>
              <td className="py-2 px-2 text-right">
                {totalOrcado > 0 ? `${((totalDiferenca / totalOrcado) * 100).toFixed(1)}%` : '-'}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header do Relatório */}
      <Card className="bg-white border-stone-200 print:shadow-none">
        <div className="p-6 border-b border-stone-200 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white print:bg-indigo-600 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <FileText className="w-6 h-6" />
                Relatório de Acompanhamento Orçamentário
              </h2>
              <p className="text-indigo-100 mt-1">{propriedade?.nome}</p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold">{ano}</p>
              <p className="text-sm text-indigo-200">
                Gerado em {format(new Date(), "dd/MM/yyyy HH:mm", { locale: ptBR })}
              </p>
            </div>
          </div>
        </div>

        {/* Resumo Executivo */}
        <CardContent className="p-6">
          <h3 className="font-bold text-lg text-stone-800 mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-indigo-600" />
            Resumo Executivo
          </h3>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="p-4 bg-green-50 rounded-xl border border-green-200">
              <p className="text-xs text-green-600">Receitas Orçadas</p>
              <p className="text-xl font-bold text-green-700">{formatCurrency(dadosRelatorio.totalReceitasOrcadas)}</p>
              <p className="text-sm text-green-600 mt-1">
                Real: {formatCurrency(dadosRelatorio.totalReceitasRealizadas)}
              </p>
            </div>
            <div className="p-4 bg-red-50 rounded-xl border border-red-200">
              <p className="text-xs text-red-600">Despesas Orçadas</p>
              <p className="text-xl font-bold text-red-700">{formatCurrency(dadosRelatorio.totalDespesasOrcadas)}</p>
              <p className="text-sm text-red-600 mt-1">
                Real: {formatCurrency(dadosRelatorio.totalDespesasRealizadas)}
              </p>
            </div>
            <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
              <p className="text-xs text-blue-600">Resultado Orçado</p>
              <p className="text-xl font-bold text-blue-700">{formatCurrency(dadosRelatorio.resultadoOrcado)}</p>
            </div>
            <div className={`p-4 rounded-xl border ${dadosRelatorio.resultadoRealizado >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
              <p className="text-xs text-stone-600">Resultado Realizado</p>
              <p className={`text-xl font-bold ${dadosRelatorio.resultadoRealizado >= 0 ? 'text-emerald-700' : 'text-amber-700'}`}>
                {formatCurrency(dadosRelatorio.resultadoRealizado)}
              </p>
            </div>
          </div>

          {/* Tabela de Receitas */}
          {dadosRelatorio.receitas.length > 0 && (
            <div className="mb-6">
              <h4 className="font-bold text-green-700 mb-3 flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                RECEITAS
              </h4>
              {renderTabela(dadosRelatorio.receitas, 'receita')}
            </div>
          )}

          {/* Tabela de Despesas */}
          {dadosRelatorio.despesas.length > 0 && (
            <div className="mb-6">
              <h4 className="font-bold text-red-700 mb-3 flex items-center gap-2">
                <TrendingDown className="w-5 h-5" />
                DESPESAS
              </h4>
              {renderTabela(dadosRelatorio.despesas, 'despesa')}
            </div>
          )}

          {/* Alertas */}
          {(dadosRelatorio.receitas.some(r => Math.abs(r.desvio) > (r.limite_alerta || 10)) ||
            dadosRelatorio.despesas.some(d => Math.abs(d.desvio) > (d.limite_alerta || 10))) && (
            <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
              <h4 className="font-bold text-amber-700 mb-2 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Itens com Desvio Significativo
              </h4>
              <ul className="space-y-1 text-sm">
                {[...dadosRelatorio.receitas, ...dadosRelatorio.despesas]
                  .filter(item => Math.abs(item.desvio) > (item.limite_alerta || 10))
                  .map(item => (
                    <li key={item.id} className="flex justify-between">
                      <span>{item.categoria} ({item.tipo})</span>
                      <span className={item.desvio > 0 ? 'text-red-600' : 'text-green-600'}>
                        {item.desvio >= 0 ? '+' : ''}{item.desvio.toFixed(1)}% - {formatCurrency(item.diferenca)}
                      </span>
                    </li>
                  ))}
              </ul>
            </div>
          )}

          {/* Botão Imprimir */}
          <div className="flex justify-end mt-6 print:hidden">
            <Button onClick={handlePrint} className="bg-indigo-600 hover:bg-indigo-700 gap-2">
              <Printer className="w-4 h-4" />
              Imprimir Relatório
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}